import { computed, makeObservable } from "mobx";
import type { WithAppStore } from "@/store/types";
import {
  type PlatformEntitlementsData,
  SubscriptionPlanKind,
  type SubscriptionPlanVariant,
  type SubscriptionTier,
} from "@/store/subscriptions/types";

export class FeatureAccessObservable {
  private data: PlatformEntitlementsData;
  private store: WithAppStore["store"];

  constructor({ data, store }: { data: PlatformEntitlementsData } & WithAppStore) {
    this.data = data;
    this.store = store;

    makeObservable<this, "data" | "store">(this, {
      data: false,
      store: false,
      tier: computed,
      storedPlanKind: computed,
      planKind: computed,
      planVariant: computed,
      startsAt: computed,
      expiresAt: computed,
      isPaid: computed,
      isFree: computed,
      isPro: computed,
      isStoredProactive: computed,
      isProactive: computed,
    });
  }

  get tier(): SubscriptionTier {
    return this.store.platformInfo.subscriptionWasCompletedSuccessfully === true
      ? "paid"
      : this.data.tier;
  }

  get storedPlanKind(): SubscriptionPlanKind {
    return this.data.plan_kind;
  }

  get planKind(): SubscriptionPlanKind {
    if (this.store.platformInfo.subscriptionWasCompletedSuccessfully === true) {
      return this.store.platformInfo.completedSubscriptionPlanKind ?? SubscriptionPlanKind.PRO;
    }

    return this.storedPlanKind;
  }

  get planVariant(): SubscriptionPlanVariant | null {
    if (!this.data.plan_variant) {
      return null;
    }

    return this.data.plan_variant as SubscriptionPlanVariant;
  }

  get startsAt(): string {
    return this.data.starts_at;
  }

  get expiresAt(): string | null {
    return this.data.expires_at;
  }

  get isPaid(): boolean {
    return (
      this.planKind === SubscriptionPlanKind.PRO ||
      this.planKind === SubscriptionPlanKind.PROACTIVE ||
      this.tier === "paid" ||
      this.data.is_paid
    );
  }

  get isFree(): boolean {
    return this.planKind === SubscriptionPlanKind.FREE;
  }

  get isPro(): boolean {
    return this.planKind === SubscriptionPlanKind.PRO;
  }

  get isStoredProactive(): boolean {
    return this.storedPlanKind === SubscriptionPlanKind.PROACTIVE;
  }

  get isProactive(): boolean {
    return this.planKind === SubscriptionPlanKind.PROACTIVE || this.data.is_proactive;
  }
}
